/**
 * Planned Outreach: the drips, call missions and campaigns a deal or a customer is in, and what each does next.
 * The deal page and Customer 360 show the same section in their sidebar; `UpNext` shows the planned touches above
 * a timeline. The server decides every fact (`app/services/outreach.py`); this only words it.
 */

import { Link } from "react-router-dom";
import { Mail, MessageSquare, Phone } from "lucide-react";

import { RecordSection } from "@/components/records/record-section";
import {
  ICON,
  ROW,
  GUTTER,
  T_BODY,
  T_ERROR,
  T_EYEBROW,
  T_META,
} from "@/components/records/visual-system";
import { StatusPill } from "@/components/ui/status-pill";
import type {
  Outreach,
  OutreachItem,
  OutreachNext,
} from "@/hooks/use-outreach";
import { cn } from "@/lib/utils";

import {
  KIND_LABEL,
  nextLine,
  splitItems,
  stepLabel,
  stopLabel,
  touchLabel,
  upNext,
  whenLabel,
  whoLabel,
} from "./outreach-lines";

function OutreachRow({ item }: { item: OutreachItem }) {
  const step = stepLabel(item);
  return (
    <li className="space-y-0.5 py-2 first:pt-0" data-testid="outreach-row">
      <div className="flex items-start justify-between gap-2">
        {item.href ? (
          <Link
            to={item.href}
            className={cn(
              T_BODY,
              "min-w-0 font-semibold text-primary hover:underline",
            )}
          >
            {item.name}
          </Link>
        ) : (
          <span className={cn(T_BODY, "min-w-0 font-semibold")}>
            {item.name}
          </span>
        )}
        <StatusPill tone="neutral" className="shrink-0">
          {KIND_LABEL[item.kind]}
        </StatusPill>
      </div>
      {item.next ? <p className={T_BODY}>{nextLine(item.next)}</p> : null}
      {item.next || step ? (
        <p className={T_META}>
          {[step, item.next ? whoLabel(item.next) : null]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}
      {item.note && item.state === "held" ? (
        <StatusPill tone="warning" className="whitespace-normal text-left">
          {item.note}
        </StatusPill>
      ) : item.note ? (
        <p className={T_META}>{item.note}</p>
      ) : null}
    </li>
  );
}

/** The sidebar section. Renders nothing once loaded with nothing to say, so a customer in no outreach sees no
 *  empty box. */
export function PlannedOutreach({
  titleId,
  data,
  isLoading,
  isError,
}: {
  titleId: string;
  data: Outreach | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) return null;
  if (isError) {
    return (
      <RecordSection
        titleId={titleId}
        title="Planned Outreach"
        testId="planned-outreach"
      >
        <p className={T_ERROR}>
          Couldn’t load the drips, missions and campaigns.
        </p>
      </RecordSection>
    );
  }
  const { live, ended } = splitItems(data?.items ?? []);
  const stops = data?.stops ?? [];
  if (!live.length && !ended.length && !stops.length) return null;
  return (
    <RecordSection
      titleId={titleId}
      title="Planned Outreach"
      count={live.length ? `${live.length} Active` : undefined}
      testId="planned-outreach"
    >
      <div className="space-y-2">
        {stops.length ? (
          <div className="flex flex-wrap gap-1.5">
            {stops.map((stop) => (
              <StatusPill
                key={stop.channel}
                tone="danger"
                className="whitespace-normal text-left"
              >
                {stopLabel(stop)}
              </StatusPill>
            ))}
          </div>
        ) : null}
        {live.length ? (
          <ul className="divide-y divide-border/60">
            {live.map((item) => (
              <OutreachRow key={item.key} item={item} />
            ))}
          </ul>
        ) : (
          <p className={T_META}>Nothing planned right now.</p>
        )}
        {ended.length ? (
          <details className={T_META} data-testid="outreach-ended">
            <summary className="cursor-pointer font-medium">
              Recently Ended ({ended.length})
            </summary>
            <ul className="mt-1 space-y-1">
              {ended.map((item) => (
                <li key={item.key}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="text-primary hover:underline"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    item.name
                  )}{" "}
                  ({KIND_LABEL[item.kind]}){item.note ? `: ${item.note}` : ""}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </RecordSection>
  );
}

const CHANNEL_ICON = {
  email: Mail,
  text: MessageSquare,
  phone: Phone,
} as const;

function UpNextRow({ item, next }: { item: OutreachItem; next: OutreachNext }) {
  const Icon = CHANNEL_ICON[next.channel as keyof typeof CHANNEL_ICON] ?? Mail;
  const when = whenLabel(next);
  return (
    <li
      className={cn(
        ROW,
        "rounded-md border border-dashed border-border px-2 py-1.5",
      )}
      data-testid="up-next-row"
    >
      <span className={GUTTER}>
        <Icon className={ICON} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className={T_BODY}>
          {touchLabel(next)} · {item.name}
        </p>
        <p className={T_META}>
          {[when, whoLabel(next), item.state === "held" ? item.note : "Planned"]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <span />
    </li>
  );
}

/** The planned touches above a timeline: what goes out next, before anything that already happened. */
export function UpNext({ data }: { data: Outreach | undefined }) {
  const rows = upNext(data?.items ?? []);
  if (!rows.length) return null;
  return (
    <section aria-label="Up Next" className="space-y-1.5" data-testid="up-next">
      <h4 className={T_EYEBROW}>Up Next</h4>
      <ul className="space-y-1.5">
        {rows.map((item) => (
          <UpNextRow
            key={item.key}
            item={item}
            next={item.next}
          />
        ))}
      </ul>
    </section>
  );
}
